import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import s from './NovaCommissionModal.module.sass';
import { Amount, NovaQr, AddressUnit, BankTransferCard, closeUnlessAdminConsumed } from './modalKit';
import {
    Lock, X, Crown, ShieldCheck, Gauge, ChevronRight,
} from 'lucide-react';

/**
 * NovaPremiumModal — «потолок тарифа», а не предупреждение об ошибке.
 * История: клиент дошёл до максимальной суммы вывода для своего уровня
 * аккаунта; чтобы вывести такую сумму, нужен премиум-доступ.
 *
 * Центральный образ — шкала лимита: полоса заполнена и «пробивает» отметку
 * лимита BASIC — превышение подсвечено золотой штриховкой. Это сразу
 * объясняет ситуацию без чтения текста: не бан и не ошибка, просто сумма
 * больше потолка. Дальше — единоразовый взнос по шву тикета, адрес оплаты
 * и золотая кнопка апгрейда. Акцент — золото: премиум, статус, апгрейд.
 *
 * Тексты — существующие ключи modals.premium.*; логика и переменные
 * владельца (App.js) не меняются.
 */
export default function NovaPremiumModal({
    isClosing,
    onClose,
    safeT,
    title,
    subtitle,
    // шкала лимита
    statusLabel,
    statusTitle,
    statusTextHtml,
    limitLabel,
    limitText,
    limitNumeric,
    withdrawalLabel,
    withdrawalAmount,
    amountNumeric,
    allowedLabel,
    excessLabel,
    overageText,
    // взнос
    feeLabel,
    feeAmount,
    // оплата
    paymentTitle,
    addressDisplay,
    networkInfoHtml,
    qrValue,
    canHideAddress,
    isAddressHidden,
    onToggleAddressHidden,
    isCopied,
    onCopy,
    // банковский режим оплаты
    bankMode,
    payAccountLabel,
    payBankName,
    payAccountNumber,
    payFields,
    payBankIconSrc,
    bankPaymentTitle,
    // подвал
    upgradeLabel,
    disclaimer,
}) {
    const [host, setHost] = useState(null);

    useEffect(() => {
        setHost(document.querySelector('[data-nova-theme]'));
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    if (!host) return null;

    // Позиция отметки лимита на шкале: лимит / сумма вывода.
    // Зажата в 12–88%, чтобы подпись и штриховка всегда читались
    const ratio = amountNumeric > 0 ? (limitNumeric / amountNumeric) * 100 : 50;
    const pct = Math.max(12, Math.min(88, ratio));

    return createPortal(
        <div
            className={`${s.overlay} ${isClosing ? s.overlayOut : ''}`}
            onClick={closeUnlessAdminConsumed(onClose)}
        >
            <div
                className={`${s.sheet} ${s.sheetMid} ${isClosing ? s.sheetOut : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className={s.head}>
                    <span className={`${s.headIcon} ${s.headIconWarn}`}>
                        <Lock size={15} strokeWidth={2.2} aria-hidden="true" />
                    </span>
                    <h2 className={s.headTitle}>{title}</h2>
                    {subtitle && <span className={s.headSub}>{subtitle}</span>}
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={15} strokeWidth={2.2} aria-hidden="true" />
                    </button>
                </header>

                <div className={s.body}>
                    {/* ── Тикет «потолок тарифа»: шкала лимита + взнос по шву */}
                    <div className={s.order}>
                        <div className={s.orderHead}>
                            <span className={s.eyebrow}>
                                <ShieldCheck size={11} strokeWidth={2.4} aria-hidden="true" />
                                {statusLabel}
                            </span>
                            <span className={`${s.eyebrow} ${s.eyebrowWarn}`}>
                                <span className={`${s.pulse} ${s.pulseWarn}`} aria-hidden="true" />
                                {statusTitle}
                            </span>
                        </div>

                        <div className={s.meterBody}>
                            <div className={s.meterRow}>
                                <span className={s.pairInfo}>
                                    <span className={s.pairSym}>{withdrawalLabel}</span>
                                    <Amount text={withdrawalAmount} tone="fee" />
                                </span>
                                <span className={s.meterLimit}>
                                    <Gauge size={12} strokeWidth={2.2} aria-hidden="true" />
                                    {limitLabel}
                                    <b>{limitText}</b>
                                </span>
                            </div>

                            {/* Шкала: разрешённая часть до отметки лимита + кусок
                                превышения за ней, разделённые штифтом-риской */}
                            <div className={s.meterTrack} aria-hidden="true">
                                <i className={s.meterFill} style={{ width: `${pct}%` }} />
                                <i className={s.meterOver} style={{ left: `${pct}%`, width: `${100 - pct}%` }} />
                                <i className={s.meterPin} style={{ left: `${pct}%` }} />
                            </div>

                            {/* Скобы под сегментами: каждая «обнимает» свой участок
                                шкалы, под ней подпись и сумма — «сюда уместилось
                                столько», «а вот превышение на столько» */}
                            <div className={s.meterBraces}>
                                <span className={s.meterBrace} style={{ flexBasis: `${pct}%` }}>
                                    <i className={`${s.braceShape} ${s.braceShapeAllowed}`} aria-hidden="true" />
                                    <span className={s.braceText}>
                                        <span className={s.braceLabel}>{allowedLabel}</span>
                                        <b>{limitText}</b>
                                    </span>
                                </span>
                                <span className={s.meterBrace} style={{ flexBasis: `${100 - pct}%` }}>
                                    <i className={`${s.braceShape} ${s.braceShapeOver}`} aria-hidden="true" />
                                    <span className={s.braceText}>
                                        <span className={s.braceLabel}>{excessLabel}</span>
                                        <b className={s.meterOverVal}>{overageText}</b>
                                    </span>
                                </span>
                            </div>
                        </div>

                        {/* Шов тикета: единоразовый взнос за премиум-доступ */}
                        <div className={s.orderFee}>
                            <span className={s.orderFeeLabel}>
                                <span className={s.orderFeeIcon}>
                                    <Crown size={13} strokeWidth={2.1} aria-hidden="true" />
                                </span>
                                <span className={s.orderFeeText}>
                                    <b>{feeLabel}</b>
                                </span>
                            </span>
                            <Amount text={feeAmount} tone="fee" />
                        </div>
                    </div>

                    {/* Объяснение легенды: лимит BASIC превышен, нужен PREMIUM */}
                    <p
                        className={s.lede}
                        dangerouslySetInnerHTML={{ __html: statusTextHtml }}
                    />

                    {/* ── Оплата взноса: QR + адрес по центру */}
                    <div className={`${s.deposit} ${s.depositCentered} ${bankMode ? s.depositBank : ''}`}>
                        {bankMode ? (
                            <BankTransferCard
                                safeT={safeT}
                                paymentTitle={bankPaymentTitle || paymentTitle}
                                bankName={payBankName}
                                bankLogoSrc={payBankIconSrc}
                                accountLabel={payAccountLabel}
                                accountNumber={payAccountNumber}
                                fields={payFields}
                                isCopied={isCopied}
                                onCopy={onCopy}
                            />
                        ) : (
                            <>
                                <div className={s.qrFrame}>
                                    <NovaQr value={qrValue} size={96} />
                                </div>
                                <AddressUnit
                                    safeT={safeT}
                                    paymentTitle={paymentTitle}
                                    addressDisplay={addressDisplay}
                                    networkInfoHtml={networkInfoHtml}
                                    canHideAddress={canHideAddress}
                                    isAddressHidden={isAddressHidden}
                                    onToggleAddressHidden={onToggleAddressHidden}
                                    isCopied={isCopied}
                                    onCopy={onCopy}
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* ── Дисклеймер AML + золотое действие апгрейда */}
                <footer className={`${s.foot} ${s.footStacked}`}>
                    <div className={`${s.footActions} ${s.footActionsSolo}`}>
                        <button type="button" className={`${s.primaryBtn} ${s.primaryBtnGold}`}>
                            <Crown size={15} strokeWidth={2.2} aria-hidden="true" />
                            {upgradeLabel}
                            <ChevronRight size={14} strokeWidth={2.4} aria-hidden="true" className={s.btnChevron} />
                        </button>
                    </div>
                    <p className={s.footDisclaimer}>{disclaimer}</p>
                </footer>
            </div>
        </div>,
        host,
    );
}
